import React, {FC, useContext, useEffect, useState} from 'react'
import Listing, {getRequestFilters} from "Components/Listing/Listing";
import Header from "../Components/Layout/Header";
import {useTranslation} from "react-i18next";
import ListingSetting from "../Entity/Collection/ListingSetting";
import BimboShareModal from "../Components/Modal/BimboShareModal";
import {UserContext} from "../Vendor/Context/UserContextProvider";
import {BannerContext} from "../Vendor/Context/BannerContextProvider";

import EntityInterface from "../Vendor/Definition/EntityInterface";
import BimboShare from "../Entity/BimboShare";
import {useLocation, useSearchParams} from "react-router-dom";
import Api from "../Vendor/Api";
import DeleteModal from "../Components/Modal/DeleteModal";

import useBodyClass from 'Hooks/useBodyClass'

export interface BimboShareProps {
}

const BimboSharePage: FC<BimboShareProps> = () => {
    const {currentUser} = useContext(UserContext)
    const {setBanner} = useContext(BannerContext)
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()
    const [search, setSearch] = useState<string>('')
    const [isExporting, setIsExporting] = useState<boolean>(false)
    const [setting] = useState<ListingSetting>({
        cardCondensedByDefault: false,
        cardShowBadge: false,
        cardShowStatus: false,
        cardShowSchedule: true,
        cardShowSizeAndWeight: null,
        filters: [
            {
                slug: 'createdAt',
                filters: 'createdAt',
            },
            {
                slug: 'createdBy',
                filters: 'createdBy',
            },
            {
                slug: 'brand',
                filters: 'brand',
            },
        ],
        groupedActions: [],
        kanbanDatas: [],
        cardDatas: [],
        searchable: [],
        searchableByEmail: false,
        searchableByFirstName: false,
        searchableByLastName: false,
        searchableByName: false,
        sorters: [
            {
                sorter: 'createdAt',
                defaultSort: true,
                defaultDirection: 'desc'
            },
            {
                sorter: 'updatedAt',
                defaultSort: false,
                defaultDirection: 'desc'
            },
            {
                sorter: 'name',
                defaultSort: false,
                defaultDirection: 'asc'
            },
        ],
        tableColumns: [
            {
                column: 'status',
                visibleByDefault: true,
            },
            {
                columnType: 'name',
                visibleByDefault: true,
                translated: true,
            },
            {
                columnType: 'createdAt',
                visibleByDefault: true,
            },
            {
                column: 'expiredAt',
                visibleByDefault: true,
                render: (bimboShare: BimboShare) => {
                    if (bimboShare.status === 'draft') {
                        return <i>N/A</i>
                    }
                    return bimboShare.expiredAt ? (new Date(bimboShare.expiredAt)).toLocaleDateString() : <i>-</i>
                }
            },
            {
                column: 'brand',
                visibleByDefault: !!(currentUser && currentUser.currentOrganization && currentUser.currentOrganization.brands && currentUser.currentOrganization.brands.length > 1),
                field: 'name',
            },
            {
                column: 'platform',
                visibleByDefault: !!(currentUser && currentUser.currentOrganization && currentUser.currentOrganization.platforms && currentUser.currentOrganization.platforms.length > 1),
                field: 'name',
            },
            {
                column: 'views',
                visibleByDefault: false,
            },
            {
                column: 'download',
                visibleByDefault: false,
            },
        ],
        views: ['table'],
    })
    const [toggleReload, setToggleReload] = useState<string>('')
    const [currentBimboShare, setCurrentBimboShare] = useState<string|undefined>(undefined)
    const [showBimboShare, setShowBimboShare] = useState<boolean>(false)
    useBodyClass('showBimboShareModal', showBimboShare)
    const location = useLocation()
    const [bimboShare, setBimboShare] = useState<BimboShare>({} as BimboShare)
    const [showDelete, setShowDelete] = useState<boolean>(false)

    useEffect(() => {
        if (showBimboShare) {
            document.body.classList.add('showBimboShareModal')
            const timer = setTimeout(() => {
                document.body.classList.add('showBimboShareModal')
                const modal = document.getElementById('bimboShareModal')
                if (modal) {
                    modal.style.top = window.scrollY + 'px'
                }
            }, 0)
            return () => clearTimeout(timer)
        } else {
            document.body.classList.remove('showBimboShareModal')
        }
    }, [showBimboShare, location.pathname, location.search])

    useEffect(() => {
        if (location.state
            && typeof location.state === 'object'
            && location.state.hasOwnProperty('bimboShare')
        ) {
            let state = location.state as { bimboShare: string }
            if (typeof state.bimboShare === "string" && state.bimboShare.length > 0) {
                handleOpenModal(state.bimboShare)
                window.history.replaceState({}, document.title)
            }
        }
    }, [location.state])

    const handleOpenModal = (tempCurrentBimboShare?: string) => {
        setCurrentBimboShare(tempCurrentBimboShare)
        setShowBimboShare(true)
        document.body.classList.add('showBimboShareModal')
        const updateModalPosition = () => {
            document.body.classList.add('showBimboShareModal')
            let modal = document.getElementById('bimboShareModal')
            if (modal) {
                modal.style.top = window.scrollY + 'px'
            }
        }
        updateModalPosition()
        setTimeout(updateModalPosition, 10)
        setTimeout(updateModalPosition, 100)
    }

    const handleDeleteClose = (reload = false) => {
        setBimboShare({} as BimboShare)
        setShowDelete(false)
        if (reload) {
            setToggleReload(new Date().toISOString())
        }
    }

    const handleDeleteShow = () => setShowDelete(true)

    const deleteBimboShare = (bimboShare: EntityInterface) => {
        setBimboShare(bimboShare as BimboShare)
        handleDeleteShow()
    }

    const handleCloseModal = (refresh: boolean = false) => {
        document.body.classList.remove('showBimboShareModal')
        if (refresh) {
            setToggleReload(new Date().toISOString())
        }
        setShowBimboShare(false)
        setCurrentBimboShare(undefined)
    }

    const handleExport = () => {
        if (isExporting) {
            return
        }

        let filters = {}
        const filterParam = searchParams.get('filters')
        if (filterParam) {
            try {
                filters = getRequestFilters(JSON.parse(filterParam))
            } catch (e) {}
        } else {
            const stored = localStorage.getItem('filters./bimbo-share')
            if (stored) {
                try {
                    filters = getRequestFilters(JSON.parse(stored))
                } catch (e) {}
            }
        }

        setIsExporting(true)
        Api.post('/bimbo-share/export', {
            filters,
            keyword: search,
            sortName: 'createdAt',
            sortWay: 'decreasing',
        }, {
            responseType: 'blob',
        })
            .then((response) => {
                let filename = 'bimbo-shares.xlsx'
                const disposition = response.headers['content-disposition']
                if (disposition) {
                    const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '').trim()
                    }
                }
                const href = URL.createObjectURL(response.data)
                const link = document.createElement('a')
                link.href = href
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()

                document.body.removeChild(link)
                URL.revokeObjectURL(href)
                setBanner({type: 'success', i18nTitle: 'app.save', i18nMessage: 'app.data_saved_message'})
            })
            .catch(() => {
                setBanner({type: 'error', i18nTitle: 'app.error', i18nMessage: 'app.error_occurred'})
            })
            .finally(() => {
                setIsExporting(false)
            })
    }

    return <>
        <Header
            title={t('app.bimboShare')}
            search={search}
            setSearch={setSearch}
            exportModalButton={{
                icon: isExporting ? 'fa-solid fa-circle-notch fa-spin' : 'fa-solid fa-file-spreadsheet',
                onClick: handleExport,
            }}
            add={() => {
                handleOpenModal(undefined)
            }}
        />
        <Listing
            key={toggleReload || 'initial'}
            path="/bimbo-share"
            search={search}
            setSearch={setSearch}
            setting={setting}
            selectable={false}
            toggleReload={toggleReload}
            setToggleReload={setToggleReload}
            editAction={(item) => {
                let bimboShare = item as BimboShare
                handleOpenModal(bimboShare.uuid)
            }}
            deleteAction={deleteBimboShare}
        />
        {showBimboShare && <BimboShareModal
            key={currentBimboShare ?? 'new'}
            handleClose={handleCloseModal}
            currentBimboShare={currentBimboShare}
            setCurrentBimboShare={setCurrentBimboShare}
        />}
        <DeleteModal
            handleClose={handleDeleteClose}
            show={showDelete}
            path={'/bimbo-share/' + (bimboShare?.uuid ?? 'error')}
            name={bimboShare?.name || (bimboShare?.uuid ? `#${bimboShare.uuid}` : '')}
            archive={true}
        />
    </>
}

export default BimboSharePage
